import React, { useState, useEffect } from 'react';
import { FiUsers, FiPlus, FiEdit2, FiUserPlus, FiTrash2 } from 'react-icons/fi';
import AddTeamModal from '../components/AddTeamModal';
import TeamDetailModal from '../components/TeamDetailModal';
import InviteUserModal from '../components/InviteUserModal';
import './TeamMembers.css';

const TeamMembers = ({ token, user }) => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, teamsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
      if (teamsRes.ok) {
        const data = await teamsRes.json();
        setTeams(data.teams || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });
      
      if (response.ok) {
        fetchData();
        setEditingRole(null);
      }
    } catch (error) {
      console.error('Failed to update role:', error);
    }
  };

  const handleColorChange = async (userId, newColor) => {
    try {
      // Optimistic update
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, color: newColor } : u)));
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/color`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ color: newColor }),
      });
    } catch (error) {
      console.error('Failed to update color:', error);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchData();
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user');
    }
  };

  const handleAddTeam = () => {
    fetchData();
  };

  const isAdmin = user?.role === 'Admin';

  if (loading) return <div className="loading-spinner"></div>;

  return (
    <div className="team-members-page">
      <div className="page-header-with-actions">
        <h1 className="team-members-title">Team Members</h1>
        {isAdmin && (
          <button 
            className="btn btn-primary"
            onClick={() => setShowInviteModal(true)}
            data-testid="invite-user-button"
          >
            <FiUserPlus size={18} />
            <span>Invite User</span>
          </button>
        )}
      </div>
      
      <div className="section-card">
        <div className="section-header">
          <h2 className="section-title">Team Members ({users.length})</h2>
          {isAdmin && (
            <span className="section-helper">Click role to change permissions</span>
          )}
        </div>
        <div className="members-list">
          {users.map((member) => (
            <div key={member.id} className="member-item" data-testid="member-item">
              <div className="avatar" style={{ backgroundColor: member.color || undefined }}>{member.name?.charAt(0).toUpperCase() || 'U'}</div>
              <div className="member-info">
                <div className="member-name">{member.name}</div>
                <div className="member-email">{member.email}</div>
              </div>
              {(isAdmin || user?.role === 'PM') && (
                <input
                  type="color"
                  className="member-color-input"
                  value={member.color || '#0F0F10'}
                  onChange={(e) => handleColorChange(member.id, e.target.value)}
                  title="Member signature color"
                  data-testid="member-color-input"
                />
              )}
              {editingRole === member.id && isAdmin ? (
                <select
                  className="input"
                  defaultValue={member.role}
                  autoFocus
                  onBlur={(e) => handleRoleChange(member.id, e.target.value)}
                  onChange={(e) => handleRoleChange(member.id, e.target.value)}
                  style={{ width: '180px' }}
                  data-testid="role-select"
                >
                  <option value="Admin">Admin</option>
                  <option value="PM">PM</option>
                  <option value="Site Engineer">Site Engineer</option>
                  <option value="Designer">Designer</option>
                  <option value="Client">Client</option>
                </select>
              ) : (
                <span 
                  className={`member-role ${isAdmin ? 'editable' : ''}`}
                  onClick={() => isAdmin && setEditingRole(member.id)}
                  data-testid="member-role"
                >
                  {member.role}
                  {isAdmin && <FiEdit2 size={12} style={{ marginLeft: '6px' }} />}
                </span>
              )}
              {isAdmin && member.id !== user.id && (
                <button
                  className="delete-member-btn"
                  onClick={() => handleDeleteUser(member.id, member.name)}
                  title="Delete user"
                  data-testid="delete-member-button"
                >
                  <FiTrash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="section-card" style={{ marginTop: 'var(--space-6)' }}>
        <div className="section-header">
          <h2 className="section-title">Teams ({teams.length})</h2>
          {(isAdmin || user?.role === 'PM') && (
            <button 
              className="btn btn-primary btn-sm"
              onClick={() => setShowAddTeamModal(true)}
              data-testid="create-team-button"
            >
              <FiPlus size={14} />
              <span>New Team</span>
            </button>
          )}
        </div>
        <div className="teams-grid">
          {teams.map((team) => (
            <div 
              key={team.id} 
              className="team-card" 
              onClick={() => setSelectedTeam(team)}
              data-testid="team-card"
            >
              <div className="team-card-icon">
                <FiUsers size={24} />
              </div>
              <div className="team-card-name">{team.name}</div>
              <div className="team-card-desc">{team.description}</div>
              <div className="team-card-count">{team.members?.length || 0} members</div>
            </div>
          ))}
        </div>
      </div>
      
      <AddTeamModal
        isOpen={showAddTeamModal}
        onClose={() => setShowAddTeamModal(false)}
        onAdd={handleAddTeam}
        token={token}
      />
      
      <TeamDetailModal
        isOpen={!!selectedTeam}
        onClose={() => setSelectedTeam(null)}
        team={selectedTeam}
        token={token}
        onUpdate={fetchData}
      />
      
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        token={token}
        onInvite={fetchData}
      />
    </div>
  );
};

export default TeamMembers;
